import React from 'react';
import Icon from 'components/AppIcon';

const RISK_LEVELS = [
  {
    value: 'conservative',
    label: 'Conservative',
    description: 'Prioritize safety, minimal risk',
    icon: 'Shield',
    color: '#10B981',
  },
  {
    value: 'moderate',
    label: 'Moderate',
    description: 'Balanced growth and security',
    icon: 'BarChart2',
    color: '#06B6D4',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    description: 'Maximize growth, higher risk',
    icon: 'TrendingUp',
    color: '#F59E0B',
  },
];

const NOTIFICATION_OPTIONS = [
  { key: 'budgetAlerts', label: 'Budget Limit Alerts', description: 'Notify when approaching spending limits', icon: 'Bell' },
  { key: 'weeklyReport', label: 'Weekly Spending Report', description: 'Summary of your weekly expenses', icon: 'FileText' },
  { key: 'goalMilestones', label: 'Goal Milestones', description: 'Celebrate when you hit savings targets', icon: 'Trophy' },
  { key: 'arScanTips', label: 'AR Scan Insights', description: 'Tips after product scans', icon: 'ScanLine' },
  { key: 'financialTips', label: 'Financial Education Tips', description: 'Weekly personal finance tips', icon: 'BookOpen' },
];

const PLAN_OPTIONS = [
  {
    value: 'free',
    label: 'Free Plan',
    price: '$0/mo',
    features: ['Basic budgeting', '5 AR scans/month', 'Standard insights'],
    icon: 'Star',
    color: '#94A3B8',
  },
  {
    value: 'premium',
    label: 'Premium Plan',
    price: '$9.99/mo',
    features: ['Unlimited AR scans', 'AI-powered insights', 'Goal tracking', 'Priority support'],
    icon: 'Zap',
    color: '#06B6D4',
    recommended: true,
  },
];

export default function PreferencesStep({ data, onChange }) {
  const notifications = data?.notifications || {};

  const toggleNotification = (key) => {
    onChange({ ...data, notifications: { ...notifications, [key]: !notifications?.[key] } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-[#F8FAFC] mb-1">Risk Tolerance</h3>
        <p className="text-sm text-[#94A3B8] mb-4">
          How comfortable are you with financial risk? This helps us tailor your investment recommendations.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {RISK_LEVELS?.map((level) => (
            <button
              key={level?.value}
              onClick={() => onChange({ ...data, riskTolerance: level?.value })}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all duration-200 min-h-[44px] ${
                data?.riskTolerance === level?.value
                  ? 'border-[rgba(6,182,212,0.5)] bg-[rgba(6,182,212,0.1)]'
                  : 'border-[rgba(6,182,212,0.12)] bg-[rgba(15,23,42,0.5)] hover:border-[rgba(6,182,212,0.25)]'
              }`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${level?.color}20` }}
              >
                <Icon name={level?.icon} size={20} color={level?.color} strokeWidth={2} />
              </div>
              <span className="text-sm font-semibold text-[#F8FAFC]">{level?.label}</span>
              <span className="text-xs text-[#94A3B8] text-center">{level?.description}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-base font-semibold text-[#F8FAFC] mb-1">Notification Preferences</h3>
        <p className="text-sm text-[#94A3B8] mb-4">Choose what updates you'd like to receive.</p>
        <div className="space-y-2">
          {NOTIFICATION_OPTIONS?.map((opt) => (
            <button
              key={opt?.key}
              onClick={() => toggleNotification(opt?.key)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-[rgba(6,182,212,0.12)] bg-[rgba(15,23,42,0.5)] hover:border-[rgba(6,182,212,0.25)] transition-all min-h-[44px] text-left"
            >
              <Icon name={opt?.icon} size={18} color={notifications?.[opt?.key] ? '#06B6D4' : '#475569'} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#CBD5E1]">{opt?.label}</p>
                <p className="text-xs text-[#64748B]">{opt?.description}</p>
              </div>
              <div
                className={`w-10 h-5 rounded-full transition-all duration-200 flex-shrink-0 relative ${
                  notifications?.[opt?.key] ? 'bg-[#06B6D4]' : 'bg-[rgba(6,182,212,0.15)]'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200 ${
                    notifications?.[opt?.key] ? 'left-5' : 'left-0.5'
                  }`}
                />
              </div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-base font-semibold text-[#F8FAFC] mb-1">Choose Your Plan</h3>
        <p className="text-sm text-[#94A3B8] mb-4">Start free or unlock the full FinVisAR experience.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PLAN_OPTIONS?.map((plan) => (
            <button
              key={plan?.value}
              onClick={() => onChange({ ...data, plan: plan?.value })}
              className={`relative flex flex-col gap-3 p-4 rounded-lg border transition-all duration-200 text-left min-h-[44px] ${
                data?.plan === plan?.value
                  ? 'border-[#06B6D4] bg-[rgba(6,182,212,0.1)]'
                  : 'border-[rgba(6,182,212,0.15)] bg-[rgba(15,23,42,0.5)] hover:border-[rgba(6,182,212,0.3)]'
              }`}
            >
              {plan?.recommended && (
                <span className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#06B6D4] text-[#0F172A]">
                  Recommended
                </span>
              )}
              <div className="flex items-center gap-2">
                <Icon name={plan?.icon} size={20} color={plan?.color} />
                <span className="text-sm font-semibold text-[#F8FAFC]">{plan?.label}</span>
                <span className="font-data text-sm font-bold text-[#06B6D4] ml-auto">{plan?.price}</span>
              </div>
              <ul className="space-y-1">
                {plan?.features?.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-[#94A3B8]">
                    <Icon name="Check" size={12} color="#10B981" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}